import React, { useEffect, useMemo } from "react";
import { Card, Nav, Spinner, Button } from "react-bootstrap";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Entradas from "./Entradas";
import styled from "styled-components";
import {
  DetalleTransporteDeFecha,
  QUERY_OBTENER_TRANSPORTES_POR_FECHA,
  esEntradaDeFecha,
  modificarHojaDiaria,
  ModificacionesHojaDiaria,
  ModificacionesDetalleTransporteDiario,
  HojaDeTransportePorFecha,
} from "../../../services/transporteServicios";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import DetalleModal from "./DetalleModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ModificacionesDetalle } from "./Modificaciones";
import SuccessButton from "../../organisms/SuccessButton";
import Salidas from "./Salidas";
import { buildValueChange } from "../../../utils/ValueChange";
import { formatearFechaStr } from "../../../utils/dateUtils";
import EdicionDetalleModal from "./EdicionDetalleModal";
import IconContainer from "../../organisms/IconContainer";
import SaveIcon from '@mui/icons-material/Save';

function EntradasSalidas({
  hojaDeTransporte,
  toggleMostrarCalendario,
}: {
  hojaDeTransporte: HojaDeTransportePorFecha;
  toggleMostrarCalendario: (mostrar: boolean) => void;
}) {
  const queryClient = useQueryClient();

  const [tipoDetalle, setTipoDetalle] = React.useState<"entradas" | "salidas">(
    "entradas"
  );

  const [detalleSeleccionado, setDetalleSeleccionado] = React.useState<
    DetalleTransporteDeFecha | undefined
  >(undefined);

  const [modificacionesEntradas, setModificacionesEntradas] = React.useState<
    Map<string, ModificacionesDetalle> | undefined
  >(undefined);

  const [modificacionesSalidas, setModificacionesSalidas] = React.useState<
    Map<string, ModificacionesDetalle> | undefined
  >(undefined);

  const modificacionesMutacion = useMutation(modificarHojaDiaria, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_OBTENER_TRANSPORTES_POR_FECHA],
      });
    },
  });

  const obtenerOCrearModificacionesEntradas = () => {
    let modEntradas = modificacionesEntradas;
    if (!modEntradas) {
      modEntradas = new Map<string, ModificacionesDetalle>();
    } else {
      modEntradas = new Map(modEntradas);
    }

    return modEntradas;
  };

  const obtenerOCrearModificacionesSalidas = () => {
    let modEntradas = modificacionesSalidas;
    if (!modEntradas) {
      modEntradas = new Map<string, ModificacionesDetalle>();
    } else {
      modEntradas = new Map(modEntradas);
    }

    return modEntradas;
  };

  const obtenerOrdenDetalle = (
    detalle: DetalleTransporteDeFecha,
    modificaciones: Map<string, ModificacionesDetalle>
  ) => {
    const mods = modificaciones.get(detalle.id);
    if (mods?.orden) {
      return mods!.orden!;
    }
    return detalle.orden;
  };

  const onSubirOrdenDetalle = (
    detalle: DetalleTransporteDeFecha,
    anterior: DetalleTransporteDeFecha
  ) => {
    let modDetalle = esEntradaDeFecha(detalle)
      ? obtenerOCrearModificacionesEntradas()
      : obtenerOCrearModificacionesSalidas();

    const ordenAnterior = obtenerOrdenDetalle(anterior, modDetalle);
    const ordenEntrada = obtenerOrdenDetalle(detalle, modDetalle);

    let modsActualesDet = modDetalle!.get(detalle.id);

    if (modsActualesDet === undefined) {
      modsActualesDet = {
        detalle: detalle,
        orden: ordenAnterior,
      };
    } else {
      modsActualesDet = {
        ...modsActualesDet,
        orden: ordenAnterior,
      };
    }
    modDetalle.set(detalle.id, modsActualesDet!);

    // bajar detalle anterior
    let modsActualesDetAnterior = modDetalle!.get(anterior.id);
    if (modsActualesDetAnterior === undefined) {
      modsActualesDetAnterior = {
        detalle: anterior,
        orden: ordenEntrada,
      };
    } else {
      modsActualesDetAnterior = {
        ...modsActualesDetAnterior,
        orden: ordenEntrada,
      };
    }
    modDetalle.set(anterior.id, modsActualesDetAnterior!);

    if (esEntradaDeFecha(detalle)) {
      setModificacionesEntradas(modDetalle);
    } else {
      setModificacionesSalidas(modDetalle);
    }
  };

  const onBajarOrdenDetalle = (
    detalle: DetalleTransporteDeFecha,
    siguiente: DetalleTransporteDeFecha
  ) => {
    let modDetalle = esEntradaDeFecha(detalle)
      ? obtenerOCrearModificacionesEntradas()
      : obtenerOCrearModificacionesSalidas();

    const ordenSiguiente = obtenerOrdenDetalle(siguiente, modDetalle);
    const ordenEntrada = obtenerOrdenDetalle(detalle, modDetalle);

    let modsActualesDet = modDetalle!.get(detalle.id);
    if (modsActualesDet === undefined) {
      modsActualesDet = {
        detalle: detalle,
        orden: ordenSiguiente,
      };
    } else {
      modsActualesDet = {
        ...modsActualesDet,
        orden: ordenSiguiente,
      };
    }
    modDetalle.set(detalle.id, modsActualesDet!);

    // subir la siguiente entrada
    let modsActualesDetSiguiente = modDetalle!.get(siguiente.id);
    if (modsActualesDetSiguiente === undefined) {
      modsActualesDetSiguiente = {
        detalle: siguiente,
        orden: ordenEntrada,
      };
    } else {
      modsActualesDetSiguiente = {
        ...modsActualesDetSiguiente,
        orden: ordenEntrada,
      };
    }
    modDetalle.set(siguiente.id, modsActualesDetSiguiente!);
    if (esEntradaDeFecha(detalle)) {
      setModificacionesEntradas(modDetalle);
    } else {
      setModificacionesSalidas(modDetalle);
    }
  };

  const onChequearDetalle = (
    detalle: DetalleTransporteDeFecha,
    transportado: boolean
  ) => {
    let modDetalle = esEntradaDeFecha(detalle)
      ? obtenerOCrearModificacionesEntradas()
      : obtenerOCrearModificacionesSalidas();

    let modsActualesDet = modDetalle!.get(detalle.id);
    if (modsActualesDet === undefined) {
      modsActualesDet = {
        detalle: detalle,
        transportado: transportado,
      };
    } else {
      modsActualesDet = {
        ...modsActualesDet,
        transportado: transportado,
      };
    }
    modDetalle.set(detalle.id, modsActualesDet!);

    if (esEntradaDeFecha(detalle)) {
      setModificacionesEntradas(modDetalle);
    } else {
      setModificacionesSalidas(modDetalle);
    }
  };

  const onGuardarClick = () => {
    const modsEntradas: ModificacionesDetalleTransporteDiario[] =
      modificacionesEntradas
        ? Array.from(modificacionesEntradas.values()).map((modDetalle) => {
          return {
            id: modDetalle.detalle.id,
            orden:
              modDetalle.orden !== undefined
                ? buildValueChange(
                  modDetalle.detalle.orden,
                  modDetalle.orden!
                )
                : undefined,
            transportado:
              modDetalle.transportado !== undefined
                ? buildValueChange(
                  modDetalle.detalle.transportado,
                  modDetalle.transportado!
                )
                : undefined,
          };
        })
        : [];

    const modsSalidas: ModificacionesDetalleTransporteDiario[] =
      modificacionesSalidas
        ? Array.from(modificacionesSalidas.values()).map((modDetalle) => {
          return {
            id: modDetalle.detalle.id,
            orden:
              modDetalle.orden !== undefined
                ? buildValueChange(
                  modDetalle.detalle.orden,
                  modDetalle.orden!
                )
                : undefined,
            transportado:
              modDetalle.transportado !== undefined
                ? buildValueChange(
                  modDetalle.detalle.transportado,
                  modDetalle.transportado!
                )
                : undefined,
          };
        })
        : [];

    const modificacionesHojaDiaria: ModificacionesHojaDiaria = {
      id: hojaDeTransporte!.id,
      entradas: modsEntradas,
      salidas: modsSalidas,
    };

    modificacionesMutacion.mutate(modificacionesHojaDiaria);
  };

  const onDetalleClick = (detalle: DetalleTransporteDeFecha | undefined) => {
    setDetalleSeleccionado(detalle);
  };

  useEffect(() => {
    if (modificacionesMutacion.isLoading) {
      setModificacionesEntradas(undefined);
      setModificacionesSalidas(undefined);
    }
  }, [modificacionesMutacion]);

  const DetalleScreen = useMemo(() => {
    if (detalleSeleccionado) {
      if (detalleSeleccionado.transportado) {
        return (
          <DetalleModal
            detalle={detalleSeleccionado}
            onCloseClick={() => onDetalleClick(undefined)}
          />
        );
      } else {
        return (
          <EdicionDetalleModal
            idHoja={hojaDeTransporte.id}
            detalle={detalleSeleccionado}
            onCloseClick={() => onDetalleClick(undefined)}
          />);
      }

    } else {
      return <></>;
    }
  }, [detalleSeleccionado, hojaDeTransporte.id]);

  const error = modificacionesMutacion.error;
  const mutacionIsLoading = modificacionesMutacion.isLoading;

  if (error) {
    return <ServerErrorModal />;
  }

  return (
    <>
      <Card>
        <StyledCardHeader className="row justify-content-between">
          <div className="d-flex col-3 justify-content-start">
            <div className="align-self-end">
              <Button
                variant="primary"
                onClick={() => {
                  toggleMostrarCalendario(true);
                }}
                size="sm"
              >
                <>
                  {mutacionIsLoading && (
                    <Spinner size="sm" animation="border" />
                  )}
                  {!mutacionIsLoading && (<IconContainer style={{ color: 'white' }}>
                    <CalendarMonthIcon />
                  </IconContainer>)}
                </>
              </Button>
            </div>
          </div>
          <div className="d-flex col-6 justify-content-center m-0 p-0">
            <div className="row m-0 text-center">
              <h5>{formatearFechaStr(hojaDeTransporte.fecha)}</h5>
            </div>
          </div>
          <div className="d-flex col-3 justify-content-end">
            <div className="align-self-end">
              <SuccessButton
                variant="success"
                onClick={onGuardarClick}
                size="sm"
                disabled={modificacionesEntradas === undefined && modificacionesSalidas === undefined}
              >
                <>
                  {mutacionIsLoading && (
                    <Spinner size="sm" animation="border" />
                  )}
                  {!mutacionIsLoading && (<IconContainer style={{ color: 'white' }}>
                    <SaveIcon />
                  </IconContainer>)}
                </>
              </SuccessButton>
            </div>
          </div>
        </StyledCardHeader>
        <Card.Body>
          <Nav variant="tabs" defaultActiveKey={"entradas"}>
            <Nav.Item>
              <Nav.Link
                eventKey={"entradas"}
                onClick={() => {
                  setTipoDetalle("entradas");
                }}
              >
                Entradas
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey={"salidas"}
                onClick={() => {
                  setTipoDetalle("salidas");
                }}
              >
                Salidas
              </Nav.Link>
            </Nav.Item>
          </Nav>
          {tipoDetalle === "entradas" && !mutacionIsLoading && (
            <Entradas
              entradas={hojaDeTransporte?.entradas || []}
              onDetalleClick={onDetalleClick}
              onChequearEntrada={onChequearDetalle}
              modificacionesEntradas={modificacionesEntradas}
              onSubirOrden={onSubirOrdenDetalle}
              onBajarOrden={onBajarOrdenDetalle}
            />
          )}
          {tipoDetalle === "salidas" && !mutacionIsLoading && (
            <Salidas
              salidas={hojaDeTransporte?.salidas || []}
              onDetalleClick={onDetalleClick}
              onChequearSalida={onChequearDetalle}
              modificacionesSalidas={modificacionesSalidas}
              onSubirOrden={onSubirOrdenDetalle}
              onBajarOrden={onBajarOrdenDetalle}
            />
          )}
        </Card.Body>
      </Card>
      {DetalleScreen}
    </>
  );
}

export default EntradasSalidas;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  padding: 1.25rem 1.25rem 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 0px;
`;
